import SearchField from '@/components/common/SearchField';
import { useApplication } from '@/hooks/useApplication';
import { useMetadata } from '@/hooks/useMetadata';
import { useSubroutine } from '@/hooks/useSubroutine';
import { Prompt, PromptId, PromptSortType } from '@/types/prompt';
import { VariableRef } from '@/types/variable';
import { findSubroutine } from '@/util/application';
import { filterPrompts, promptIsInUse, promptSearchOptions } from '@/util/prompts';
import { sortPrompts } from '@/util/sort';
import { isString } from '@/util/typeGuards';
import { ChevronRight, ExpandMore } from '@mui/icons-material';
import CircleIcon from '@mui/icons-material/Circle';
import { Box, List, ListItemButton, ListItemText, ListSubheader, Tooltip } from '@mui/material';
import { FC, JSX, useEffect, useState } from 'react';
import { useLocalStorage } from 'usehooks-ts';

type PromptsListProps = {
  value?: VariableRef | number;
  onClick: (promptId: PromptId) => void;
  onDoubleClick: (promptId: PromptId) => void;
};

const PromptsList: FC<PromptsListProps> = (props) => {
  const { application } = useApplication();
  const { metadata } = useMetadata();
  const { selectedSubroutineId } = useSubroutine();
  const currentSubroutine = findSubroutine(application.subroutines, selectedSubroutineId);

  const [headerVisibility, setHeaderVisibility] = useState<Record<string, boolean>>({});
  const [prompts, setPrompts] = useState<Prompt[]>();
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [sortBy, setSortBy] = useLocalStorage<PromptSortType>('promptSort', 'label');
  const [invert, setInvert] = useLocalStorage<boolean>('promptInvert', false);

  const CustomListSubHeader: FC<{
    title: string;
    groupId: number;
  }> = (props) => {
    const isHeaderVisible = headerVisibility[props.title] !== false;

    return (
      <ListSubheader
        onClick={() => {
          setHeaderVisibility((prevVisibility) => ({
            ...prevVisibility,
            [props.title]: !isHeaderVisible
          }));
        }}
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          cursor: 'pointer',
          '&:hover': {
            backgroundColor: 'action.hover'
          }
        }}
      >
        <Box>{props.title}</Box>
        {isHeaderVisible ? <ExpandMore /> : <ChevronRight />}
      </ListSubheader>
    );
  };

  const renderPrompt = (prompts: Prompt[], title: string, groupId: number) => {
    const isHeaderVisible = headerVisibility[title] !== false;

    return (
      <Box key={`prompt + ${title} + ${groupId}`}>
        <CustomListSubHeader title={title} groupId={groupId} />
        {isHeaderVisible &&
          prompts.map((prompt) => {
            const PromptIsUsed = promptIsInUse(application, metadata, currentSubroutine, prompt);
            return (
              <ListItemButton
                key={`prompt.${prompt.id}`}
                selected={prompt.id === props.value}
                onClick={() => props.onClick(prompt.id)}
                onDoubleClick={() => props.onDoubleClick(prompt.id)}
                sx={{ gap: 1 }}
              >
                <Tooltip
                  title={
                    PromptIsUsed
                      ? PromptIsUsed === 'green'
                        ? 'Prompt is in use'
                        : 'Prompt is in use elsewhere'
                      : 'Prompt is unused'
                  }
                >
                  <CircleIcon sx={{ color: PromptIsUsed ?? 'transparent', pr: '5px' }} />
                </Tooltip>
                <ListItemText
                  primary={getPromptName(prompt)}
                  secondary={
                    <code>
                      {prompt.path && (isString(prompt.path) ? prompt.path + '/' : `[${prompt.path?.name}]/`)}
                      {prompt.file}
                    </code>
                  }
                />
              </ListItemButton>
            );
          })}
      </Box>
    );
  };

  const getPrompts = (sortedPrompts?: Prompt[]) => {
    const result: JSX.Element[] = [];
    application.promptGroups?.forEach((group) => {
      const filteredPrompts = sortedPrompts?.filter((prompt) => prompt.promptGroup === group.id);
      filteredPrompts && result.push(renderPrompt(filteredPrompts, group.name, group.id));
    });

    const grouplessPrompts = sortedPrompts?.filter((prompt) => !prompt.promptGroup || prompt.promptGroup === 0);
    grouplessPrompts && result.push(renderPrompt(grouplessPrompts, 'All', 0));
    return result;
  };

  const filteredPrompts = filterPrompts(prompts, searchQuery);

  const getPromptName = (prompt: Prompt) => {
    let value = prompt.promptGroup
      ? application.promptGroups?.find((group) => group.id === prompt.promptGroup)?.name + ' / '
      : '';
    value += prompt.label || prompt.file;

    return value;
  };

  useEffect(() => {
    setPrompts(application.prompts);
  }, [application.prompts]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: '1 1 auto', minHeight: 0 }}>
      {application?.prompts.length > 2 && (
        <Box mb={2}>
          <SearchField
            searchOptions={promptSearchOptions}
            searchQuery={searchQuery}
            setSearchQuery={(query) => setSearchQuery(query)}
            sortBy={sortBy}
            invert={invert}
            onSort={(changeSortBy, reverse) => {
              changeSortBy && setSortBy(changeSortBy as PromptSortType);
              reverse && setInvert(!invert);
            }}
            autoFocus
          />
        </Box>
      )}
      <List sx={{ width: '100%', flex: '1 1 auto', minHeight: 0, overflowY: 'auto', pt: 0 }}>
        {getPrompts(sortPrompts(filteredPrompts, sortBy, invert, 'file'))}
      </List>
    </Box>
  );
};

export default PromptsList;
